import { CellComponent, Options, RowComponent } from 'tabulator-tables';
import { MassiaTabulatorColumnDef, MassiaTabulatorFeatures, MassiaTabulatorGridConfig } from '../massia.tabulator.type';

// Constante pour la pagination en bas de page
export const MASSIA_TABULATOR_LANGS = {
	default: {
		pagination: {
			first: "<i class='fa fa-angle-double-left'></i>",
			first_title: 'Première Page',
			last: "<i class='fa fa-angle-double-right'></i>",
			last_title: 'Dernière Page',
			prev: "<i class='fa fa-angle-left'></i>",
			prev_title: 'Page Précédente',
			next: "<i class='fa fa-angle-right'></i>",
			next_title: 'Page Suivante',
			page_size: 'Lignes par page',
			page_title: 'Afficher la page'
		},
		data: {
			loading: 'Chargement en cours...',
			error: 'Erreur lors du chargement'
		}
	}
};

/**
 * Contexte requis pour construire les options Tabulator
 */
export interface MassiaTabulatorOptionsContext {
	/** Configuration Massia définie (provenant du serveur ou configurée localement) */
	gridConfig: MassiaTabulatorGridConfig;
	/** Données locales (si fournies en local) */
	dataset?: unknown[];
	/** Surcharge d'options natives Tabulator venant du front-end */
	optionsOverrides?: Partial<Options>;
	/** Service de notification pour certains comportements (ex: clipboard) */
	notification: { success: (m: string) => void; error: (m: string) => void };
	/** Callback pour capturer le nombre total d'éléments lors d'un appel AJAX (pour le compteur) */
	captureRemoteTotal: (total: number) => void;
	/** Élément HTML dédié au compteur de pagination calculé à la volée */
	getCounterEl: () => HTMLElement | null;
}

export class MassiaTabulatorOptionsBuilder {
	/**
	 * Construit le gros objet "Options" requis par le constructeur native de Tabulator
	 * en se basant sur la configuration métier Massia.
	 *
	 * @param context Contexte d'initialisation
	 * @param columns Colonnes préalablement traitées par MassiaTabulatorColumnsBuilder
	 * @returns L'objet Options native de Tabulator
	 */
	static build(context: MassiaTabulatorOptionsContext, columns: MassiaTabulatorColumnDef[]): Options {
		const config = context.gridConfig;
		const features: MassiaTabulatorFeatures = config.features || {};

		// Initialisation base Tabulator
		const options: Record<string, unknown> = {
			height: features.height ?? '65vh',
			columns: columns,
			columnDefaults: { resizable: true },
			langs: MASSIA_TABULATOR_LANGS,
			layout: 'fitColumns',
			...config.options, // Surcharges provenant du GridConfig (potentiellement du backend)
			...context.optionsOverrides // Surcharges provenant du HTML <massia-tabulator options="...">
		};

		// ─── Données Locales ─────────────────────────────────────────────────────
		if (features.localMode === true && features.remote?.enabled) {
			features.remote.enabled = false;
		}

		// En mode remote, les données ne doivent pas être fournies pendant l'initialisation afin d'éviter de désactiver la pagination.
		if (!features.remote?.enabled) {
			options['data'] = context.dataset || [];
		}

		// ─── Persistance ─────────────────────────────────────────────────────────
		if (features.persistence?.enabled) {
			options['persistence'] = true;
			options['persistenceID'] = features.persistence.id;
			options['persistenceMode'] = 'local';
		}

		// ─── Pagination Distante (Remote) ────────────────────────────────────────
		if (features.remote?.enabled) {
			options['pagination'] = true;
			options['paginationMode'] = 'remote';
			options['filterMode'] = 'remote';
			options['sortMode'] = 'remote';
			options['paginationSize'] = features.remote.pageSize ?? 200;
			options['paginationInitialPage'] = features.remote.page ?? 1;

			if (features.remote.sort) options['initialSort'] = features.remote.sort;
			if (features.remote.filter) options['initialHeaderFilter'] = features.remote.filter;

			options['paginationCounter'] = function (
				_pageSize: number,
				_currentRow: number,
				currentPage: number,
				totalRows: number,
				totalPages: number
			) {
				return `${currentPage} / ${totalPages} pages (${totalRows} élément${totalRows > 1 ? 's' : ''})`;
			};
			options['paginationSizeSelector'] = [100, 200, 500, 1000];
			options['paginationButtonCount'] = 1;

			// Élément de pagination externe
			if (features.remote.element) {
				const el: HTMLElement | null =
					typeof features.remote.element === 'string'
						? (document.querySelector(features.remote.element as string) as HTMLElement)
						: (features.remote.element as HTMLElement);

				if (el) {
					el.innerHTML = '';
					options['paginationElement'] = el;

					// On s'assure de lier le tag .massia-page-counter
					let counterSpan = el.querySelector('.massia-page-counter');
					if (!counterSpan) {
						counterSpan = document.createElement('span');
						counterSpan.className = 'massia-page-counter';
						el.appendChild(counterSpan);
					}
					// L'instance appelante a besoin de cette référence, on l'utilise si on l'a (geré par le Manager)
				}
			}

			if (features.remote.ajaxURL) options['ajaxURL'] = features.remote.ajaxURL;

			if (features.remote?.fetchFn) {
				const originalFetchFn = features.remote.fetchFn;
				options['ajaxRequestFunc'] = (url: string, cfg: unknown, params: Record<string, unknown>) => originalFetchFn(url, cfg, params);
			}

			// Capture de la réponse réseau pour injecter le select-all côté client
			options['ajaxResponse'] = (url: string, params: Record<string, unknown>, response: { data?: unknown[]; last_row?: number }) => {
				context.captureRemoteTotal(response.last_row || 0);

				// Application du select-all générique pour les données distantes
				if (config && config.columns) {
					const activeSelectAlls = config.columns.filter((c) => c._selectAllState?.active).map((c) => c._selectAllState!);

					if (activeSelectAlls.length > 0 && response.data && Array.isArray(response.data)) {
						response.data.forEach((row: Record<string, unknown>) => {
							activeSelectAlls.forEach((state) => {
								if (!state.enabledWhenField || row[state.enabledWhenField]) {
									row[state.field] = true;
								}
							});
						});
					}
				}
				return response;
			};
		}

		// ─── Menu Contextuel Ligne (Clic Droit) ──────────────────────────────────
		options['rowContextMenu'] = [
			{
				label: 'Copier la ligne (JSON)',
				action: (_e: MouseEvent, row: RowComponent) => navigator.clipboard.writeText(JSON.stringify(row.getData(), null, 2))
			},
			{ separator: true }
			// En commentaire pour l'insant à cause d'un problème d'UI - Les lignes vont dans le header avec la class 'tabulator-header-contents' et sont après les titres des colonnes
			/*{
				label: 'Épingler cette ligne',
				action: (_e: MouseEvent, row: RowComponent) => row.freeze(),
				disabled: (row: RowComponent) => row.isFrozen()
			},
			{
				label: 'Désépingler cette ligne',
				action: (_e: MouseEvent, row: RowComponent) => row.unfreeze(),
				disabled: (row: RowComponent) => !row.isFrozen()
			}*/
		];

		// ─── Fonctionnalités UI standard ─────────────────────────────────────────
		if (features.selection) options['selectable'] = true;
		if (features.tooltips) {
			const defaults = (options['columnDefaults'] || {}) as Record<string, unknown>;
			defaults['tooltip'] = true;
			options['columnDefaults'] = defaults;
		}
		if (features.movableRows) options['movableRows'] = true;
		if (features.groupBy) options['groupBy'] = features.groupBy;
		if (features.movableColumns !== false) options['movableColumns'] = true;

		// ─── Copie Presse-Papiers ────────────────────────────────────────────────
		if (features.clipboard) {
			options['clipboard'] = 'copy';
			options['clipboardPasteAction'] = 'replace';
			options['clipboardCopyConfig'] = { columnHeaders: false, rowGroups: false };
			options['clipboardCopied'] = () => context.notification.success('Données copiées dans le presse-papier !');
		}

		// ─── Données Arborescentes (TreeData) ────────────────────────────────────
		if (features.treeData) {
			options['dataTree'] = true;
			options['dataTreeStartExpanded'] = false;
			options['dataTreeChildField'] = '_children';
			options['dataTreeElementColumn'] = features.treeDataElementColumn ?? false;
		}

		// ─── Menu des Détails (Master-Detail) ────────────────────────────────────
		if (features.rowDetails) {
			const hasToggle = columns.some((c) => (c as MassiaTabulatorColumnDef & { formatter?: string }).formatter === 'rowDetailToggle');
			if (!hasToggle) {
				columns.unshift({
					title: '',
					formatter: (cell: CellComponent) => {
						const isOpen = cell.getRow().getElement().classList.contains('row-detail-open');
						return `<i class='fa fa-${isOpen ? 'minus' : 'plus'}-square' style='color:#666;cursor:pointer'></i>`;
					},
					width: 30,
					hozAlign: 'center',
					headerSort: false,
					frozen: true,
					cellClick: (_e: UIEvent, cell: CellComponent) => {
						_e.stopPropagation();
						const row = cell.getRow();
						const el: HTMLElement = row.getElement();
						const holder: HTMLElement | null = el.querySelector('.row-detail-holder');
						if (holder) {
							el.classList.toggle('row-detail-open');
							if (el.classList.contains('row-detail-open')) {
								holder.style.display = 'block';
								cell.getElement().innerHTML = `<i class='fa fa-minus-square' style='color:#666;cursor:pointer'></i>`;
							} else {
								holder.style.display = 'none';
								cell.getElement().innerHTML = `<i class='fa fa-plus-square' style='color:#666;cursor:pointer'></i>`;
							}
							row.normalizeHeight();
						}
					}
				} as MassiaTabulatorColumnDef);
			}

			options['rowFormatter'] = (row: RowComponent) => {
				const el: HTMLElement = row.getElement();
				if (!el.querySelector('.row-detail-holder')) {
					const holder = document.createElement('div');
					holder.className = 'row-detail-holder';
					holder.style.cssText = 'display:none;background:#fafafa;border-top:1px solid #ddd;padding:10px;margin-left:30px';
					el.appendChild(holder);
				}
			};
		}

		// ─── Coloration Lignes ───────────────────────────────────────────────────
		if (features.rowColor) {
			const prev = options['rowFormatter'] as ((row: RowComponent) => void) | undefined;
			options['rowFormatter'] = (row: RowComponent) => {
				const color = features.rowColor!(row.getData());
				if (color) row.getElement().style.backgroundColor = color;
				if (prev) prev(row);
			};
		}

		return options as Options;
	}
}
